import React, {useEffect} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {IPresentationMeta} from '../../../../incase-domain/presentation';
import AccessLinksSettings from '../../../pages/PresEditPage/components/settings/AccessLinksSettings';
import {
    PresentationSettingsFormType,
    presentationSettingsSchema,
} from '../../../../incase-domain/validators/presentation/presentationSchema';
import Button from '../../../../incase-domain/components/button/Button';
import {presentationProtectionSelector} from '../../../../store/selectors/presentation.selectors';
import {fetchPresentationProtectionAction, updatePresentationProtectionAction} from '../../../../store/actions/presentation';
import {usePatchPresentation} from '../queries';
import PresentationProtectionSettingsForm from '../../../../components/presentation/settings/PresentationProtectionSettingsForm';

interface IOnDemandConfigStepProps {
    presentation: IPresentationMeta;
    onPresentationUpdate?: (updatedPresentation: IPresentationMeta) => void;
}

function OnDemandStep({presentation, onPresentationUpdate}: IOnDemandConfigStepProps): JSX.Element {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const protection = useSelector(presentationProtectionSelector);
    const {mutateAsync: patchPresentation, isLoading} = usePatchPresentation();

    const dispatch = useDispatch();
    const formMethods = useForm<PresentationSettingsFormType>({
        defaultValues: {
            presentation,
            protection,
        },
        resolver: zodResolver(presentationSettingsSchema),
    });
    const {formState, handleSubmit, setValue, watch, getValues, reset} = formMethods;

    useEffect(() => {
        if (protection?._id !== presentation._id) {
            dispatch(fetchPresentationProtectionAction(presentation._id));
        }
    }, [presentation._id, dispatch, protection?._id]);

    useEffect(() => {
        if (protection && protection._id === presentation._id) {
            setValue('protection', protection, {shouldDirty: false, shouldValidate: true});
        }
    }, [protection, presentation._id, setValue]);

    const initialRequestParamsRef = React.useRef(presentation.requestParams ?? []);
    const initialProtectionRef = React.useRef(protection);

    useEffect(() => {
        if (protection && protection._id === presentation._id && !initialProtectionRef.current?._id) {
            initialProtectionRef.current = protection;
        }
    }, [protection, presentation._id]);

    const currentRequestParams = watch('presentation.requestParams');
    const currentProtection = watch('protection');

    const hasRequestParamsChanged = JSON.stringify(initialRequestParamsRef.current ?? []) !== JSON.stringify(currentRequestParams ?? []);
    const hasProtectionChanged = JSON.stringify(initialProtectionRef.current) !== JSON.stringify(currentProtection);

    const hasAnyChanges = hasRequestParamsChanged || hasProtectionChanged;
    const submitButtonDisabled = formState.isSubmitting || isLoading || !hasAnyChanges;

    const onSubmit: SubmitHandler<PresentationSettingsFormType> = async (data) => {
        await patchPresentation({
            presentationId: data.presentation._id,
            patch: data.presentation,
        });
        dispatch(updatePresentationProtectionAction(data.presentation._id, data.protection));
        initialRequestParamsRef.current = getValues('presentation.requestParams') ?? [];
        initialProtectionRef.current = getValues('protection');
        reset(undefined, {keepValues: true});
        onPresentationUpdate?.(data.presentation);
        queryClient.invalidateQueries(['presentationList']);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <PresentationProtectionSettingsForm useForm={formMethods} />
            <AccessLinksSettings useForm={formMethods} />
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <Button
                    variant="solid"
                    size="sm"
                    color="info"
                    wide
                    loading={formState.isSubmitting}
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={submitButtonDisabled}
                >
                    {t('common.save')}
                </Button>
            </div>
        </form>
    );
}

export default OnDemandStep;
